import { createFileRoute } from "@tanstack/react-router";
import { Phone, Mail, Clock, MapPin, ShieldCheck, Check, MessageSquare } from "lucide-react";
import { openQuoteModal } from "../components/quote-modal";
import { Reveal } from "../components/reveal";
import { Breadcrumbs } from "../components/breadcrumbs";
import { NAV_CITIES } from "../data/nav-items";
import { InternalLinkRow, POPULAR_SERVICE_LINKS, CORE_CITY_LINKS } from "@/components/internal-links";
import { InlineQuoteSection } from "@/components/inline-quote-section";

const SITE = "https://capitalpropressurewashing.com";
const TITLE = "Contact Capital Pro Pressure Washing | Albany NY (518) 900-1913";
const DESC =
  "Contact Capital Pro Pressure Washing in Albany, NY. Call (518) 900-1913 or request a free quote online. Serving the entire Capital Region with same-day estimates.";

const REASONS = [
  { icon: Clock, title: "Fast Response", desc: "Most quote requests get a reply the same day, usually within a couple of hours during business hours." },
  { icon: ShieldCheck, title: "Licensed & Insured", desc: "Full liability coverage on every job. We'll gladly send proof of insurance before we start." },
  { icon: MessageSquare, title: "Talk to the Owner", desc: "No call center. The person who quotes your job is the person who shows up to do it." },
];

const STEPS = [
  "Tell us what needs cleaning and where you're located.",
  "We confirm the scope, pricing, and a date that works for you.",
  "Our crew arrives on time, cleans, and walks the property with you.",
];

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE}/contact` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: `${SITE}/contact` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: TITLE,
          url: `${SITE}/contact`,
          description: DESC,
          mainEntity: {
            "@type": "LocalBusiness",
            name: "Capital Pro Pressure Washing",
            url: SITE,
            telephone: "+15189001913",
            priceRange: "$$",
            image: `${SITE}/__l5e/assets-v1/7fb348af-f23e-48db-88cd-6aeda94adc1a/hero-crew-surface-cleaner.jpg`,
            address: {
              "@type": "PostalAddress",
              streetAddress: "5A Vatrano Rd",
              addressLocality: "Albany",
              addressRegion: "NY",
              postalCode: "12205",
              addressCountry: "US",
            },
            areaServed: NAV_CITIES.map((c) => c.city),
            openingHoursSpecification: [
              {
                "@type": "OpeningHoursSpecification",
                dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                opens: "09:00",
                closes: "17:00",
              },
            ],
          },
        }),
      },
    ],
  }),
  component: ContactPage,
});

const H2 = "text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight";
const DISPLAY = { fontFamily: "var(--font-display)", fontWeight: 700 } as const;

function ContactPage() {
  return (
    <main>
      <Breadcrumbs items={[{ label: "Contact" }]} />

      <section className="bg-[var(--brand-deep-blue)] text-white py-14 md:py-20">
        <div className="mx-auto max-w-[1000px] px-4 text-center">
          <p className="font-semibold uppercase tracking-[0.2em] text-sm mb-3" style={{ fontFamily: "var(--font-display)", color: "#ff6b7f" }}>
            Albany &amp; the Capital Region
          </p>
          <h1 className="text-3xl md:text-5xl uppercase leading-tight" style={DISPLAY}>
            Contact Capital Pro Pressure Washing
          </h1>
          <p className="mt-4 text-white/85 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Have a question about house washing, roof washing, gutters, concrete, or a commercial
            property? Call us, or send a few details and we'll get you a free, no-pressure quote —
            usually the same day.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={() => openQuoteModal()}
              className="inline-flex items-center justify-center rounded-md bg-[#C8102E] text-white px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={DISPLAY}
            >
              Get My Free Quote
            </button>
            <a
              href="tel:+15189001913"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-white text-[var(--brand-deep-blue)] px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={DISPLAY}
            >
              <Phone size={18} strokeWidth={2.5} />
              Call (518) 900-1913
            </a>
          </div>
        </div>
      </section>

      {/* Contact details */}
      <section className="bg-white py-14 md:py-16">
        <div className="mx-auto max-w-[1000px] px-4">
          <h2 className={`${H2} text-center mb-8`} style={DISPLAY}>How to Reach Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a
              href="tel:+15189001913"
              className="rounded-lg border border-gray-200 bg-[var(--brand-light-gray)] p-6 hover:border-[var(--brand-deep-blue)] hover:bg-white transition-colors block"
            >
              <Phone size={22} className="text-[var(--brand-red)]" />
              <h3 className="mt-3 text-[var(--brand-deep-blue)] text-lg uppercase" style={DISPLAY}>Phone</h3>
              <p className="mt-1 text-[var(--brand-charcoal)] font-semibold">(518) 900-1913</p>
              <p className="mt-1 text-[var(--brand-charcoal)]/70 text-sm">
                Fastest way to reach us. Call or text for scheduling and quick questions.
              </p>
            </a>
            <div className="rounded-lg border border-gray-200 bg-[var(--brand-light-gray)] p-6">
              <Clock size={22} className="text-[var(--brand-red)]" />
              <h3 className="mt-3 text-[var(--brand-deep-blue)] text-lg uppercase" style={DISPLAY}>Hours</h3>
              <p className="mt-1 text-[var(--brand-charcoal)] font-semibold">Mon–Fri, 9am–5pm ET</p>
              <p className="mt-1 text-[var(--brand-charcoal)]/70 text-sm">
                Weekend jobs are available by appointment during the busy season.
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-[var(--brand-light-gray)] p-6">
              <MapPin size={22} className="text-[var(--brand-red)]" />
              <h3 className="mt-3 text-[var(--brand-deep-blue)] text-lg uppercase" style={DISPLAY}>Location & Service Area</h3>
              <p className="mt-1 text-[var(--brand-charcoal)] font-semibold">5A Vatrano Rd, Albany, NY 12205</p>
              <p className="mt-1 text-[var(--brand-charcoal)]/70 text-sm">
                Serving the whole Capital Region — we're a mobile service, we come to you.
              </p>
            </div>
          </div>

          <div className="mt-8 rounded-lg border border-gray-200 p-6 bg-white">
            <h3 className="text-[var(--brand-deep-blue)] text-xl uppercase" style={DISPLAY}>
              What Happens After You Reach Out
            </h3>
            <ul className="mt-4 space-y-3">
              {STEPS.map((s) => (
                <li key={s} className="flex items-start gap-3">
                  <Check size={18} strokeWidth={3} className="text-[var(--brand-red)] shrink-0 mt-1" />
                  <span className="text-[var(--brand-charcoal)] text-sm md:text-base">{s}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-[var(--brand-charcoal)]/70 text-sm">
              Most residential quotes can be given over the phone or from photos. For larger homes,
              multi-surface projects, and commercial properties we'll schedule a quick on-site walk-through
              so the number you get is the number you pay.
            </p>
          </div>
        </div>
      </section>

      {/* Why homeowners call */}
      <section className="bg-[var(--brand-light-gray)] py-14 md:py-16">
        <div className="mx-auto max-w-[1000px] px-4">
          <h2 className={`${H2} text-center mb-8`} style={DISPLAY}>Why Capital Region Homeowners Call Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {REASONS.map((r, i) => (
              <Reveal key={r.title} delayMs={i * 60} className="card-pop">
                <div className="h-full rounded-lg bg-white border border-gray-200 p-6">
                  <r.icon size={22} className="text-[var(--brand-red)]" />
                  <h3 className="mt-3 text-[var(--brand-deep-blue)] text-lg uppercase" style={DISPLAY}>{r.title}</h3>
                  <p className="mt-2 text-[var(--brand-charcoal)]/75 text-sm leading-relaxed">{r.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mt-6 text-center text-[var(--brand-charcoal)]/70 text-sm md:text-base">
            Serving {NAV_CITIES.length}+ Capital Region towns —{" "}
            <a href="/service-area" className="text-[var(--brand-deep-blue)] font-semibold hover:text-[var(--brand-bright-blue)]">
              see the full service area
            </a>{" "}
            or browse{" "}
            <a href="/services" className="text-[var(--brand-deep-blue)] font-semibold hover:text-[var(--brand-bright-blue)]">
              all of our services
            </a>.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[var(--brand-deep-blue)] text-white py-14 md:py-16">
        <div className="mx-auto max-w-[900px] px-4 text-center">
          <h2 className="text-3xl md:text-4xl uppercase leading-tight" style={DISPLAY}>
            Ready for a Free Quote?
          </h2>
          <p className="mt-3 text-white/85 max-w-2xl mx-auto">
            Tell us about your property and we'll send pricing back fast. Military and first responder
            discounts available — just mention it when you reach out.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={() => openQuoteModal()}
              className="inline-flex items-center justify-center rounded-md bg-[#C8102E] text-white px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={DISPLAY}
            >
              Get My Free Quote
            </button>
            <a
              href="tel:+15189001913"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-white text-[var(--brand-deep-blue)] px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={DISPLAY}
            >
              <Phone size={18} strokeWidth={2.5} />
              Call (518) 900-1913
            </a>
          </div>
          <p className="mt-5 text-white/60 text-xs inline-flex items-center gap-2 justify-center">
            <Mail size={14} /> Prefer to write? Use the quote form and add your question in the notes.
          </p>
        </div>
      </section>
      <InternalLinkRow
        eyebrow="Explore The Site"
        title="Popular Services"
        links={POPULAR_SERVICE_LINKS}
      />

      <InternalLinkRow
        tone="white"
        eyebrow="Where We Work"
        title="Areas We Serve"
        links={CORE_CITY_LINKS}
      />

      <InlineQuoteSection source="Contact" />
    </main>
  );
}
